import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import { useFollowAnchor } from '../../hooks/useFollowAnchor';
import { DropdownPosition, positionDropdown } from '../../utils/dropdownPosition';

export interface PublishResult {
  status: string;
  message?: string;
}

interface PublishResultToastProps {
  /** The pill the message hangs under. */
  anchorRef: React.RefObject<HTMLElement | null>;
  result: PublishResult | null;
  /** Called when the pill scrolls away or under chrome — the message goes with it. */
  onClear: () => void;
}

const WIDTH_ESTIMATE = 200;
const HEIGHT_ESTIMATE = 26;

/**
 * The Smart Publish Button's outcome line ("Published!", "Post deleted",
 * "Take requested", an error…), portaled to body and fixed under the pill.
 *
 * It used to be an absolutely positioned box inside the card header. The
 * header chrome is a stacking context (frost + backdrop blur), so the card
 * body that follows it in the DOM painted over the message: you saw its top
 * edge and nothing else (2026-10-08). Portaled, it sits at the menu's z
 * above the card, the Tended page and the item modal; positioned with the
 * menu's helper it centres under the pill and flips above when there is no
 * room; followed with the menu's hook it tracks the pill through scrolls
 * and clears the moment the pill slides under sticky chrome.
 */
const PublishResultToast: React.FC<PublishResultToastProps> = ({ anchorRef, result, onClear }) => {
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const toastRef = useRef<HTMLDivElement | null>(null);
  const measuredRef = useRef(false);

  // Width is read from the rendered toast when it exists (second pass) and
  // estimated before that (first pass) — the estimate only picks the side
  // and a provisional left; the real width re-centres it once.
  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const w = toastRef.current?.offsetWidth || WIDTH_ESTIMATE;
    const h = toastRef.current?.offsetHeight || HEIGHT_ESTIMATE;
    setPosition(positionDropdown(rect, w, h, { align: 'center' }));
  }, [anchorRef]);

  useLayoutEffect(() => {
    measuredRef.current = false;
    if (result) place();
    else setPosition(null);
  }, [result, place]);

  const mountRef = useCallback((el: HTMLDivElement | null) => {
    toastRef.current = el;
    if (el && !measuredRef.current) {
      measuredRef.current = true;
      place();
    }
  }, [place]);

  useFollowAnchor(!!result && !!position, anchorRef, place, onClear, toastRef);

  if (!result || !position) return null;
  return createPortal(
    <div
      ref={mountRef}
      role="status"
      data-publish-result={result.status}
      className="px-2.5 py-1 text-xs rounded-lg whitespace-nowrap pointer-events-none"
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        bottom: position.bottom,
        zIndex: Z.MENU,
        background: 'rgba(20,26,40,.92)',
        color: result.status === 'success' ? 'var(--signal-live)' : 'var(--signal-rec-soft)',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.12)',
      }}
    >
      {result.message}
    </div>,
    document.body,
  );
};

export default PublishResultToast;
